<template>
  <div class="relative w-full min-h-14" ref="multiSelectRoot">
    <label v-if="label !== undefined && label !== null && label !== ''" class="label">
      <span class="label-text font-medium">{{ label }}</span>
    </label>
    <button 
      type="button" 
      @click="toggleDropdownAndPosition"
      class="btn btn-outline w-full justify-start font-normal text-left py-2 px-3 min-h-0"
      :class="[{ 'btn-disabled': isLoading === true, 'ring-2 ring-primary': isOpen === true }, buttonClass]"
      aria-haspopup="listbox"
      :aria-expanded="isOpen.toString()"
      :disabled="props.disabled === true"
      :dir="activeDirection"
    >
      <span v-if="isLoading === true" class="loading loading-spinner loading-xs" :class="{'mr-2': activeDirection !== 'rtl', 'ml-2': activeDirection === 'rtl'}"></span>
      <span class="truncate flex-grow" :class="{'text-left': activeDirection !== 'rtl', 'text-right': activeDirection === 'rtl'}">{{ displaySelectedText }}</span>
      <svg class="w-4 h-4 ml-auto fill-current flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" /></svg>
    </button>

    <Teleport to="body">
      <div 
        v-if="isOpen === true" 
        ref="dropdownMenuRef"
        class="fixed z-[10050] bg-base-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg flex flex-col"
        :style="dropdownStyle"
        role="listbox"
      >
        <div class="p-2 sticky top-0 bg-base-100 dark:bg-gray-800 z-10 border-b dark:border-gray-600">
          <input 
            ref="searchInputRef"
            type="text" 
            v-model="searchTerm" 
            placeholder="Search..." 
            class="input input-sm input-bordered w-full"
            :dir="activeSearchDirection"
            :class="{'text-right': activeSearchDirection === 'rtl'}"
          />
        </div>
        <ul class="py-1 overflow-y-auto flex-grow" :style="{ maxHeight: '12rem'}" :dir="activeDirection">
          <li v-if="isLoading === true" class="px-3 py-2 text-center text-sm text-gray-500">Loading options...</li>
          <li v-else-if="filteredOptions.length === 0 && isLoading === false" class="px-3 py-2 text-center text-sm text-gray-500">No options found.</li>
          <li 
            v-for="option in filteredOptions" 
            :key="getOptionValue(option)"
            @click="toggleOption(getOptionValue(option))"
            class="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center"
            role="option"
            :aria-selected="isSelected(getOptionValue(option)) === true"
            :dir="activeDirection"
          >
            <input 
              type="checkbox" 
              :checked="isSelected(getOptionValue(option)) === true"
              class="checkbox checkbox-primary checkbox-sm pointer-events-none"
              :class="{'mr-4': activeDirection !== 'rtl', 'ml-4': activeDirection === 'rtl'}"
              tabindex="-1"
            />
            <span class="label-text w-full" :dir="activeDirection" :class="{'text-left': activeDirection !== 'rtl', 'text-right': activeDirection === 'rtl'}">{{ getOptionLabel(option) }}</span>
          </li>
        </ul>
        <slot name="after-options"></slot>
        <div v-if="(options !== undefined && options !== null && options.length > 0) && (isLoading === false) && props.maxSelections !== 1" class="p-2 sticky bottom-0 bg-base-100 dark:bg-gray-800 z-10 border-t dark:border-gray-600 flex justify-end space-x-2">
            <button @click="clearSelection" class="btn btn-xs btn-ghost">Clear All</button>
            <button @click="selectAll" class="btn btn-xs btn-ghost">Select All</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: 'Select options' },
  label: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  idField: { type: String, default: 'value' },
  labelField: { type: String, default: 'label' },
  maxDisplayedItems: { type: Number, default: 2 },
  disabled: { type: Boolean, default: false },
  maxSelections: { type: Number, default: null },
  buttonClass: { type: String, default: '' },
  forceDirection: { type: String, default: '' }
});

const emit = defineEmits(['update:modelValue']);

function getTextDirection(text) {
  if (text === null || text === undefined || text === '' || text.length === 0) return 'ltr';
  const firstChar = text.charCodeAt(0);
  const isRtlChar = (firstChar >= 0x0590 && firstChar <= 0x05FF) || (firstChar >= 0x0600 && firstChar <= 0x06FF);
  if (isRtlChar === true) {
    return 'rtl';
  } else {
    return 'ltr';
  }
}

const isOpen = ref(false);
const searchTerm = ref('');
const multiSelectRoot = ref(null);
const dropdownMenuRef = ref(null);
const searchInputRef = ref(null);
const dropdownStyle = ref({});

const textDirection = computed(() => {
  return getTextDirection(displaySelectedText.value);
});

const searchDirection = computed(() => {
  return getTextDirection(searchTerm.value);
});

const activeDirection = computed(() => {
  const forced = typeof props.forceDirection === 'string' ? props.forceDirection.trim().toLowerCase() : '';
  if (forced === 'rtl' || forced === 'ltr') {
    return forced;
  }
  return textDirection.value;
});

const activeSearchDirection = computed(() => {
  const forced = typeof props.forceDirection === 'string' ? props.forceDirection.trim().toLowerCase() : '';
  if (forced === 'rtl' || forced === 'ltr') {
    return forced;
  }
  return searchDirection.value;
});

const getOptionValue = (option) => (option !== null && option !== undefined) ? option[props.idField] : null;
const getOptionLabel = (option) => (option !== null && option !== undefined) ? option[props.labelField] : '';
const isSelected = (optionValue) => props.modelValue.includes(optionValue);

const filteredOptions = computed(() => {
  if (props.options === null || props.options === undefined) return [];
  if (searchTerm.value === '') return props.options;
  return props.options.filter(option => 
    getOptionLabel(option).toLowerCase().includes(searchTerm.value.toLowerCase())
  );
});

const displaySelectedText = computed(() => {
  if (props.modelValue.length === 0) return props.placeholder;
  const selectedLabels = props.modelValue.map(value => {
    const option = props.options.find(opt => getOptionValue(opt) === value);
    return (option !== null && option !== undefined) ? getOptionLabel(option) : value;
  });
  if (selectedLabels.length <= props.maxDisplayedItems) return selectedLabels.join(', ');
  return `${selectedLabels.slice(0, props.maxDisplayedItems).join(', ')} +${selectedLabels.length - props.maxDisplayedItems} more`;
});

const positionDropdown = () => {
  if ((multiSelectRoot.value === null || multiSelectRoot.value === undefined) || (dropdownMenuRef.value === null || dropdownMenuRef.value === undefined)) return;
  const buttonRect = multiSelectRoot.value.getBoundingClientRect();
  
  const dropdownContentMaxHeight = 270; 

  const spaceBelow = window.innerHeight - buttonRect.bottom;
  const spaceAbove = buttonRect.top;

  let topPosition;
  if (spaceBelow < dropdownContentMaxHeight && (spaceAbove > spaceBelow || spaceBelow < 100) && spaceAbove > dropdownContentMaxHeight) {
    topPosition = buttonRect.top + window.scrollY - dropdownMenuRef.value.offsetHeight - 4; 
    if (topPosition < window.scrollY) {
        topPosition = window.scrollY + 4;
    }
  } else {
    topPosition = buttonRect.bottom + window.scrollY + 4;
  }

  dropdownStyle.value = {
    position: 'fixed',
    width: `${buttonRect.width}px`,
    left: `${buttonRect.left + window.scrollX}px`,
    top: `${topPosition}px`,
  };
};

const toggleDropdownAndPosition = async () => {
  if (props.isLoading === true || props.disabled === true) return;
  if (isOpen.value === true) {
    isOpen.value = false;
  } else {
    isOpen.value = true;
    await nextTick(); 
    positionDropdown();
    await nextTick(); 
    if (searchInputRef.value !== null && searchInputRef.value !== undefined) {
      searchInputRef.value.focus();
    }
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  searchTerm.value = '';
};
defineExpose({ closeDropdown });

watch(() => props.modelValue, (newVal) => {
  if (props.maxSelections === 1) {
    closeDropdown();
  }
});

const toggleOption = (optionValue) => {
  if (props.disabled === true) return;

  if (props.maxSelections === 1) {
    if (props.modelValue.includes(optionValue)) {
      emit('update:modelValue', []);
    } else {
      emit('update:modelValue', [optionValue]);
    }
    closeDropdown();
    return;
  }

  const newValue = [...props.modelValue];
  const index = newValue.indexOf(optionValue);

  if (index === -1) {
    if (props.maxSelections === null || newValue.length < props.maxSelections) {
      newValue.push(optionValue);
    } else {
      console.warn(`Max selections (${props.maxSelections}) reached.`);
      return;
    }
  } else {
    newValue.splice(index, 1);
  }
  emit('update:modelValue', newValue);
};

const clearSelection = () => {
  if (props.disabled === true) return;
  emit('update:modelValue', []);
};

const selectAll = () => {
  if (props.disabled === true) return;
  if (props.options === null || props.options === undefined) return;

  if (props.maxSelections === null) {
    emit('update:modelValue', props.options.map(opt => getOptionValue(opt)));
  } else {
    emit('update:modelValue', props.options.slice(0, props.maxSelections).map(opt => getOptionValue(opt)));
  }
};

const handleClickOutside = (event) => {
  if (
      (multiSelectRoot.value !== null && multiSelectRoot.value !== undefined) && 
      multiSelectRoot.value.contains(event.target) === false && 
      (dropdownMenuRef.value !== null && dropdownMenuRef.value !== undefined) && 
      dropdownMenuRef.value.contains(event.target) === false
     ) {
    closeDropdown();
  }
};

watch(isOpen, (val) => {
  if (val === true) {
    window.addEventListener('resize', positionDropdown);
    window.addEventListener('scroll', positionDropdown, true);
  } else {
    window.removeEventListener('resize', positionDropdown);
    window.removeEventListener('scroll', positionDropdown, true);
  }
});

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside); 
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  window.removeEventListener('resize', positionDropdown);
  window.removeEventListener('scroll', positionDropdown, true);
  if ((dropdownMenuRef.value !== null && dropdownMenuRef.value !== undefined) && (dropdownMenuRef.value.parentNode !== null && dropdownMenuRef.value.parentNode !== undefined)) {
      
  }
});

</script>

<style scoped>
.btn-disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
.fixed.z-\[10050\] > ul {
  max-height: 12rem; 
  overflow-y: auto;
}
</style> 